import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PageContainer, PageHeader } from "../components/PageHeader";
import CarouselEditorDialog from "../components/distribution/CarouselEditorDialog";
import {
  carouselFindingText,
  saveErrorText,
} from "../components/distribution/findings";
import { friendlyError } from "../utils/apiError";
import { describeError, useToast } from "../components/toast";
import { foyerApi, type CarouselHolder, type CarouselRow } from "../http/foyer";
import { carouselWriteBody, type Carousel } from "@shared/carousel";
import { tokens } from "../theme";
import { PermissionGate } from "../auth/access";
import { AUTHOR_RAILS } from "../components/distribution/catalogueGates";

/**
 * The carousel catalogue (BE-28). Figma `NendaPortal_1` node `459-3113`.
 *
 * ## Why this list is not paginated
 *
 * Production holds ~128 carousels, hand-authored. The server sends all of them
 * and the footer ("Showing 10 of 128") is a client-side reveal, exactly as
 * the design draws it. That also means search and the type filter are instant
 * and honest — they run over the whole set rather than over the page in hand,
 * which is the lie the Distribution lists had to be corrected out of.
 *
 * ## What the columns are, and what one of them is NOT
 *
 * `Name` · `Languages` (derived from `title[].language`) · `Display limit` ·
 * `Availability` · `Type`.
 *
 * The Figma mock is in Swedish; the CMS ships in English like every other page
 * in it, so the design's Swedish is the designer's working language rather than
 * a product decision.
 *
 * ⚠️ **`Availability` is `showNonPurchasedContent`, not a date.** The mockup's
 * rows 2–10 show `2026-05-23` under that heading; that is a copy-paste error in
 * the mock and row 1 ("Visar allt") is the correct row — confirmed by Sigge,
 * 2026-08-06. **There is no availability date on a carousel.** Availability is
 * derived per room from entitlement, and the only publish flag anywhere in this
 * model is `SubPage.draft`. A rights window belongs to the asset (BE-7).
 */

const TYPE_KEYS = {
  curated: "carouselsPage.types.curated",
  query: "carouselsPage.types.query",
  epg: "carouselsPage.types.epg",
  recommended: "carouselsPage.types.recommended",
  continue_watching: "carouselsPage.types.continueWatching",
} as const;

const PAGE_STEP = 10;
const DEFAULT_DISPLAY_LIMIT = 10;

export default function CarouselsPage() {
  const toast = useToast();
  const { t } = useTranslation(["signage", "common", "content"]);
  const [rows, setRows] = useState<CarouselRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(PAGE_STEP);

  const [dialog, setDialog] = useState<{ editing: CarouselRow | null } | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<{
    code?: string;
    message: string;
  } | null>(null);

  const [rowMenu, setRowMenu] = useState<{
    anchor: HTMLElement;
    row: CarouselRow;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CarouselRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [holders, setHolders] = useState<CarouselHolder[] | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const page = await foyerApi.listCarousels();
      setRows(page.carousels);
    } catch (e) {
      const err = e as { code?: string };
      // Never an empty table on a failure: "the server is down" must not read
      // as "there are no carousels".
      setRows(null);
      setError(saveErrorText(t, err.code, friendlyError(t, e)));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.name ?? "").toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q) ||
        (r.title ?? []).some((t) => (t.text ?? "").toLowerCase().includes(q))
    );
  }, [rows, query]);

  const submit = async (draft: Carousel): Promise<boolean> => {
    setSaving(true);
    setSaveError(null);
    try {
      // ONE list, in `@shared/carousel`, covered by a test that fails when a
      // field goes missing. It was inline here, with a comment saying every
      // editor-settable field must be present — and `page` was added to the
      // model, the editor, the route, the store and the wire mapper but not to
      // this object, so the toggle never left the browser.
      const body = carouselWriteBody(draft);
      if (dialog?.editing) {
        await foyerApi.updateCarousel(dialog.editing.id, body);
      } else {
        await foyerApi.createCarousel(body);
      }
      // Re-read rather than merging locally: the server derives `languages` and
      // `issues` on every read, and a merged row would carry the verdicts of
      // the payload we sent rather than of the row that now exists.
      await load();
      const label = draft.name?.trim() || draft.id;
      toast.success(
        dialog?.editing
          ? t("carouselsPage.updated", { name: label })
          : t("carouselsPage.created", { name: label })
      );
      // The editor slides out, then calls onClose, which clears `dialog`.
      return true;
    } catch (e) {
      const err = e as { code?: string };
      setSaveError({ code: err.code, message: friendlyError(t, e) });
      toast.error(
        describeError(t, "saveCarousel", e, {
          name: draft.name?.trim() || draft.id,
        })
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openDelete = async (row: CarouselRow) => {
    setConfirmDelete(row);
    setDeleteError(null);
    setHolders(null);
    try {
      // Ask BEFORE the operator commits, so the refusal is information rather
      // than a wall.
      const usage = await foyerApi.carouselUsage(row.id);
      setHolders(usage.holders);
    } catch {
      setHolders(null);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleteError(null);
    try {
      await foyerApi.deleteCarousel(confirmDelete.id);
      setConfirmDelete(null);
      const label = confirmDelete.name?.trim() || confirmDelete.id;
      toast.success(t("carouselsPage.deleted", { name: label }));
      await load();
    } catch (e) {
      const err = e as { code?: string };
      setDeleteError(saveErrorText(t, err.code, friendlyError(t, e)));
      toast.error(
        describeError(t, "deleteCarousel", e, {
          name: confirmDelete.name?.trim() || confirmDelete.id,
        })
      );
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title={t("carouselsPage.title")}
        subtitle={t("carouselsPage.subtitle")}
        actions={
          <PermissionGate {...AUTHOR_RAILS}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSaveError(null);
                setDialog({ editing: null });
              }}
              data-testid="carousels-create"
            >
              {t("carouselsPage.create")}
            </Button>
          </PermissionGate>
        }
      />

      <Card sx={{ p: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 2.5 }}
        >
          <TextField
            size="small"
            placeholder={t("carouselsPage.searchPlaceholder")}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShown(PAGE_STEP);
            }}
            sx={{ width: 340 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            inputProps={{ "aria-label": t("carouselsPage.searchAria") }}
          />
        </Stack>

        {rows === null && !error && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={22} />
          </Stack>
        )}

        {error && (
          <Stack spacing={1.5} alignItems="flex-start" sx={{ p: 3 }}>
            <Typography sx={{ color: tokens.error }}>{error}</Typography>
            <Button size="small" onClick={() => void load()}>
              {t("carouselsPage.retry")}
            </Button>
          </Stack>
        )}

        {rows !== null && !error && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("carouselsPage.columns.name")}</TableCell>
                    <TableCell>
                      {t("carouselsPage.columns.languages")}
                    </TableCell>
                    <TableCell>
                      {t("carouselsPage.columns.displayLimit")}
                    </TableCell>
                    <TableCell>
                      {t("carouselsPage.columns.availability")}
                    </TableCell>
                    <TableCell>{t("carouselsPage.columns.type")}</TableCell>
                    <TableCell width={48} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.slice(0, shown).map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        setSaveError(null);
                        setDialog({ editing: row });
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            variant="bodySm"
                            sx={{ fontWeight: "fontWeightSemibold" }}
                          >
                            {row.name || row.id}
                          </Typography>
                          {row.issues.length > 0 && (
                            <Tooltip
                              title={row.issues
                                .map((issue) => carouselFindingText(t, issue))
                                .join(" ")}
                            >
                              <WarningIcon
                                sx={{ fontSize: 16, color: tokens.warning }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {row.languages.length === 0 ? (
                          <Typography
                            variant="bodySm"
                            sx={{ color: tokens.inkFaint }}
                          >
                            {t("common:labels.emptyValue")}
                          </Typography>
                        ) : (
                          row.languages.map((l) => l.toUpperCase()).join(", ")
                        )}
                      </TableCell>
                      <TableCell>
                        {/* A stored 0 is treated as unset everywhere else, so
                            showing "0" here would contradict the rail. */}
                        {row.displayLimit
                          ? row.displayLimit
                          : t("carouselsPage.defaultLimit", {
                              limit: DEFAULT_DISPLAY_LIMIT,
                            })}
                      </TableCell>
                      <TableCell>
                        {row.showNonPurchasedContent === false
                          ? t("carouselsPage.purchasedOnly")
                          : t("carouselsPage.showsEverything")}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            row.type in TYPE_KEYS
                              ? t(TYPE_KEYS[row.type as keyof typeof TYPE_KEYS])
                              : row.type
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <PermissionGate {...AUTHOR_RAILS}>
                          <IconButton
                            size="small"
                            aria-label={t("carouselsPage.actionsFor", {
                              name: row.name || row.id,
                            })}
                            onClick={(e) =>
                              setRowMenu({ anchor: e.currentTarget, row })
                            }
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </PermissionGate>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography
                          sx={{ color: tokens.inkFaint, py: 3 }}
                          align="center"
                        >
                          {query
                            ? t("carouselsPage.noMatch")
                            : t("carouselsPage.none")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ p: 2.5, borderTop: `1px solid ${tokens.divider}` }}
            >
              <Typography variant="bodySm" sx={{ color: tokens.inkFaint }}>
                {t("carouselsPage.showing", {
                  shown: Math.min(shown, filtered.length),
                  total: filtered.length,
                })}
              </Typography>
              {shown < filtered.length && (
                <Button
                  size="small"
                  onClick={() => setShown((n) => n + PAGE_STEP)}
                >
                  {t("carouselsPage.showMore")}
                </Button>
              )}
            </Stack>
          </>
        )}
      </Card>

      <Menu
        open={Boolean(rowMenu)}
        anchorEl={rowMenu?.anchor}
        onClose={() => setRowMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (rowMenu) {
              setSaveError(null);
              setDialog({ editing: rowMenu.row });
            }
            setRowMenu(null);
          }}
        >
          {t("common:buttons.edit")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (rowMenu) void openDelete(rowMenu.row);
            setRowMenu(null);
          }}
        >
          {t("common:buttons.delete")}
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("carouselsPage.confirmDeleteTitle", { name: confirmDelete?.name })}
        </DialogTitle>
        <DialogContent>
          {holders === null ? (
            <Typography variant="bodySm" sx={{ color: tokens.inkFaint }}>
              {t("carouselsPage.checkingUsage")}
            </Typography>
          ) : holders.length === 0 ? (
            <Typography variant="bodySm">
              {t("carouselsPage.notShown")}
            </Typography>
          ) : (
            <Stack spacing={1}>
              <Typography variant="bodySm">
                {t("carouselsPage.stillShownBy")}
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                {holders.map((h) => (
                  <li key={`${h.kind}:${h.id}`}>
                    <Typography variant="bodySm">
                      {t("carouselsPage.holder", {
                        name: h.name || h.id,
                        kind:
                          h.kind === "room-type"
                            ? t("carouselsPage.holderUnitType")
                            : t("carouselsPage.holderSubPage"),
                      })}
                    </Typography>
                  </li>
                ))}
              </Box>
              <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
                {t("carouselsPage.removeFirst")}
              </Typography>
            </Stack>
          )}
          {deleteError && (
            <Typography variant="bodySm" sx={{ color: tokens.error, mt: 2 }}>
              {deleteError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>
            {t("common:buttons.cancel")}
          </Button>
          <PermissionGate {...AUTHOR_RAILS} deniedProps={{ disabled: true }}>
            <Button
              color="error"
              variant="contained"
              disabled={holders !== null && holders.length > 0}
              onClick={() => void doDelete()}
            >
              {t("common:buttons.delete")}
            </Button>
          </PermissionGate>
        </DialogActions>
      </Dialog>

      {dialog && (
        // Remounted by key: the remount IS the reset. See [[cms-list-and-editor]].
        <CarouselEditorDialog
          key={dialog.editing?.id ?? "new"}
          open
          initial={dialog.editing}
          saving={saving}
          error={saveError}
          onClose={() => setDialog(null)}
          onSubmit={submit}
        />
      )}
    </PageContainer>
  );
}
